import React, {useContext, useEffect, useMemo, useState} from 'react';
import {EyeOutlined, HolderOutlined} from '@ant-design/icons';
import {DndContext} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {
  Table,
  Button,
  Popover,
  Divider,
  Row,
  Col,
  Image,
  Tabs,
  Tooltip,
} from 'antd';
import {useNavigate} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {
  DescriptionItem,
  ReturnNAOnEmpty,
  committeeMemberFilters,
  isEmptyObject,
} from '../../../Util/commonUtility';
import AdminActionsDropdown from '../../CommonComponents/AdminActionsDropdown/AdminActionsDropdown';
import CustomizedTable from '../../CommonComponents/CommonTable';
import AdminViewDetailsDrawer from '../../CommonComponents/AdminViewDetailsDrawer';
import {applicationConfig} from '../../../Util/config';
import useLocalization from '../../../Localization';
import {updateCommitteeMemberSequence} from '../../../Store/Actions/committeeMember';
import DynamicSearchForm from '../../CommonComponents/DynamicSearch';
import HeaderSearchCard from '../../CommonComponents/HeaderSearchCard';
import {CREATE} from '../../../Constant/enums';
import {committeeMemberFormUrl} from './committeeMemberUrls';

const ABOUT_US_TYPE_MAP = {
  leadership: 'PBSS Leadership',
  operations: 'Operations Team',
  distinguish_advisor: 'Distinguished Advisors',
  program_management: 'Program Management',
};

const CommitteeMemberComponent = (props) => {
  const {
    allCommitteeMembersData,
    deleteCommitteeMember,
    chaptersOptions,
    aboutUsSubtypeOptions,
    selectedType,
    selectedSubtype,
    onTypeChange,
    onSubtypeChange,
    listQueryString,
    sortParams,
    handleTableChange,
    searchValues,
    fetchCommitteeMembers,
  } = props;
  const [open, setOpen] = useState(false);
  const [openDrawerDetails, setOpenDrawerDetails] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);
  const [updatedSequence, setUpdatedSequence] = useState({});
  const [expanded, setExpanded] = useState(false);
  const {ApplicationString} = useLocalization();
  const dispatch = useDispatch();
  const RowContext = React.createContext({});

  let navigate = useNavigate();

  const hasActiveFilters =
    searchValues && searchValues !== false && !isEmptyObject(searchValues);
  const isDragEnabled =
    sortParams?.field === 'order_number' &&
    sortParams?.order === 'ascend' &&
    !hasActiveFilters;

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleSearch = (values) => {
    fetchCommitteeMembers(values);
  };

  useEffect(() => {
    searchValues && setExpanded(true);
  }, [searchValues]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    setDataSource(allCommitteeMembersData || []);
    setOrderChanged(false);
    setUpdatedSequence({});
  }, [allCommitteeMembersData]);

  useEffect(() => {
    if (!isDragEnabled) {
      setOrderChanged(false);
      setUpdatedSequence({});
    }
  }, [isDragEnabled]);

  const DragHandle = () => {
    const {setActivatorNodeRef, listeners} = useContext(RowContext);
    if (!isDragEnabled) {
      return (
        <Tooltip title='Clear filters and sort by Order (ascending) to reorder'>
          <Button
            type='text'
            size='small'
            icon={<HolderOutlined />}
            disabled
            style={{cursor: 'not-allowed'}}
          />
        </Tooltip>
      );
    }
    return (
      <Button
        type='text'
        size='small'
        icon={<HolderOutlined />}
        style={{cursor: 'grab'}}
        ref={setActivatorNodeRef}
        {...listeners}
      />
    );
  };

  const handleEditClick = async (id) => {
    navigate(committeeMemberFormUrl(`update/${id}`, listQueryString));
  };

  const showDrawer = (record) => {
    setOpenDrawerDetails(record);
    setOpen(true);
  };

  const onCloseDrawer = () => {
    setOpen(false);
  };

  const columns = [
    {
      key: 'sort',
      align: 'center',
      width: 50,
      render: () => <DragHandle />,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Order',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 100,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Chapter Name',
      dataIndex: 'chapterName',
      key: 'chapterName',
      render: (text, record) =>
        record?.type === 'chapter' ? ReturnNAOnEmpty(text) : '—',
    },
    {
      title: 'About Us Type',
      dataIndex: 'about_us_type',
      key: 'about_us_type',
      width: 160,
      render: (text, record) => {
        if (record?.type !== 'about_us') return '—';
        if (!text) return ReturnNAOnEmpty(text);
        return ABOUT_US_TYPE_MAP[text] || ReturnNAOnEmpty(text);
      },
    },
    {
      title: 'Bio',
      dataIndex: 'details',
      key: 'details',
      render: (text) => {
        return (
          <div className='fixed-size-text'>
            <Popover
              prefixCls='extend-popover'
              content={<CustomizedTable TableData={text} />}
              trigger='hover'
              title='Bio'
              arrow={false}>
              <Button type='text' prefixCls='common-button-popover'>
                <EyeOutlined /> View Bio
              </Button>
            </Popover>
          </div>
        );
      },
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      key: 'designation',
      render: (text) => ReturnNAOnEmpty(text),
    },
    {
      title: '',
      key: 'action',
      width: '6%',
      render: (_, record) => (
        <AdminActionsDropdown
          record={record}
          editBool={true}
          deleteBool={true}
          handleEditClick={handleEditClick}
          handleDeleteClick={deleteCommitteeMember}
          text='Committee Member'
          viewBool={true}
          handleView={showDrawer}
        />
      ),
    },
  ];

  columns.forEach((column) => {
    if (column.key !== 'action' && column.key !== 'sort') {
      column.sorter = true;
      column.sortOrder =
        sortParams?.field === column.dataIndex && sortParams?.order;
    }
  });

  const DrawerContent = () => {
    const {
      name,
      details,
      designation,
      chapterName,
      profile_id,
      type,
      about_us_type,
    } = openDrawerDetails || {};

    return (
      <>
        <Row>
          <Col span={24}>
            <DescriptionItem title='Name' content={name} />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Type'
              content={
                type === 'about_us'
                  ? 'About Us'
                  : type === 'chapter'
                  ? 'Chapter'
                  : type
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Local Chapter'
              content={type === 'chapter' ? chapterName : '—'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='About Us Type'
              content={
                type === 'about_us' && about_us_type
                  ? ABOUT_US_TYPE_MAP[about_us_type] || about_us_type
                  : '—'
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem title='Designation' content={designation} />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Bio'
              content={<CustomizedTable TableData={details} />}
            />
          </Col>
        </Row>
        <Divider />
        <Row gutter={20}>
          <Col span={24}>
            <DescriptionItem
              usedForCommittee
              title='Profile Image'
              content={
                <>
                  {profile_id?.id ? (
                    <Image
                      prefixCls='admin-committee-profile-image'
                      crossOrigin='anonymous'
                      key={profile_id?.id}
                      width={'100%'}
                      height={'400px'}
                      src={`${applicationConfig.UPLOAD_URL}/${profile_id?.name}`}
                    />
                  ) : (
                    <p>No Profile Image Uploaded</p>
                  )}
                </>
              }
            />
          </Col>
        </Row>
        <Divider />
      </>
    );
  };

  const SortableRow = ({dataRowKey, ...rowProps}) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({id: dataRowKey, disabled: !isDragEnabled});

    const style = {
      ...rowProps.style,
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? {position: 'relative', zIndex: 9999} : {}),
    };

    const contextValue = useMemo(
      () => ({
        setActivatorNodeRef,
        listeners: isDragEnabled ? listeners : undefined,
      }),
      [setActivatorNodeRef, listeners, isDragEnabled],
    );

    return (
      <RowContext.Provider value={contextValue}>
        <tr {...rowProps} ref={setNodeRef} style={style} {...attributes} />
      </RowContext.Provider>
    );
  };

  const onDragEnd = ({active, over}) => {
    if (!isDragEnabled || active.id === over?.id) return;
    setDataSource((prevState) => {
      const activeIndex = prevState.findIndex(
        (record) => record.id === active?.id,
      );
      const overIndex = prevState.findIndex((record) => record.id === over?.id);
      const reorderedData = arrayMove(prevState, activeIndex, overIndex);
      const sequence = reorderedData.reduce((acc, item, index) => {
        acc[item.id] = index + 1;
        return acc;
      }, {});
      setUpdatedSequence(sequence);
      setOrderChanged(true);
      return reorderedData;
    });
  };

  const handleSaveOrder = () => {
    dispatch(updateCommitteeMemberSequence({sequence: updatedSequence}));
    setOrderChanged(false);
  };

  const subtypeItems =
    selectedType === 'chapter'
      ? (chaptersOptions || []).map((c) => ({
          key: String(c.value),
          label: c.label,
        }))
      : (aboutUsSubtypeOptions || []).map((t) => ({
          key: t.value,
          label: t.label,
        }));

  return (
    <div className='main-container'>
      <AdminViewDetailsDrawer
        content={DrawerContent}
        visible={open}
        text={'Committee Member'}
        onCloseDrawer={onCloseDrawer}
        editNavigate={committeeMemberFormUrl(
          `update/${openDrawerDetails?.id}`,
          listQueryString,
        )}
      />
      <HeaderSearchCard
        resource='committee_members'
        toggleClick={toggleExpand}
        buttonTitle={
          ApplicationString['CommitteeMemberComponent.button.addForm']
        }
        addClick={() =>
          navigate(committeeMemberFormUrl('add', listQueryString))
        }
        dataTestId='AdFeeComponent-button-addAdFees'
        permission={CREATE}
        expanded={expanded}
      />
      {expanded && (
        <DynamicSearchForm
          columns={committeeMemberFilters}
          onSearch={handleSearch}
          savedFilteredValues={searchValues}
          title={'committeeMembers'}
        />
      )}
      <div className='admin-committee-tabs-wrap'>
        <Tabs
          className='admin-committee-tabs'
          activeKey={selectedType}
          onChange={onTypeChange}
          items={[
            {key: 'chapter', label: 'Chapter'},
            {key: 'about_us', label: 'About Us'},
          ]}
        />
        {subtypeItems.length > 0 && (
          <Tabs
            type='card'
            className='admin-committee-tabs admin-committee-tabs-subtype'
            activeKey={
              selectedSubtype !== undefined
                ? String(selectedSubtype)
                : undefined
            }
            onChange={(key) => {
              onSubtypeChange(
                selectedType === 'chapter'
                  ? isNaN(Number(key))
                    ? key
                    : Number(key)
                  : key,
              );
            }}
            items={subtypeItems}
          />
        )}
      </div>
      <div className='listing-container'>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={dataSource ? dataSource.map((item) => item.id) : []}
            strategy={verticalListSortingStrategy}
            disabled={!isDragEnabled}>
            <Table
              bordered
              className='admin-table committee-member-table'
              onChange={handleTableChange}
              components={{
                body: {
                  row: (rowProps) => (
                    <SortableRow
                      {...rowProps}
                      dataRowKey={rowProps['data-row-key']}
                    />
                  ),
                },
              }}
              columns={columns}
              dataSource={dataSource}
              rowKey={'id'}
              scroll={{x: 786, y: 'calc(100vh - 320px)'}}
              sticky={true}
              pagination={false}
              footer={() =>
                isDragEnabled && orderChanged ? (
                  <div className='refund-admin-container'>
                    <Button
                      type='primary'
                      onClick={handleSaveOrder}
                      className='common-submit-login common-form-submit'>
                      Save Order Changes
                    </Button>
                  </div>
                ) : null
              }
            />
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default CommitteeMemberComponent;
